import React from 'react'
import PropTypes from 'prop-types'
import { NavLink } from 'react-router-dom'
import { connect } from 'react-redux'
import { List as ImmutableList } from 'immutable'
import ImmutablePropTypes from 'react-immutable-proptypes'
import ImmutablePureComponent from 'react-immutable-pure-component'
import { fetchGroup } from '../actions/groups'
import PageTitle from '../features/ui/util/page_title'
import GroupLayout from '../layouts/group_layout'
import ComposeForm from '../features/compose/components/compose_form'
import Block from '../components/block'
import Text from '../components/text'
import Badge from '../components/badge'
import ColumnIndicator from '../components/column_indicator'
import GroupsPanel from '../components/panel/groups_panel'
import SidebarPanelGroup from '../components/sidebar_panel_group'
import ShortcutsPanel from '../components/panel/shortcuts_panel'
import GroupAdminPanel from '../components/panel/group_admin_panel'
import { openPopover } from '../actions/popover'
import { POPOVER_GROUP_OPTIONS } from '../constants'
import { me } from '../initial_state'

const pluralize = (base, num) => {
  if (num > 1) return `${base}s are`
  return `${base} is`
}

class GroupPage extends ImmutablePureComponent {
  
  componentDidMount() {
    this.props.dispatch(fetchGroup(this.props.params.id))
  }

  componentDidUpdate(prevProps) {
    if (prevProps.params.id !== this.props.params.id) {
      this.props.dispatch(fetchGroup(this.props.params.id))
    }
  }

  handleOnOpenGroupOptions = () => {
    const { group, relationships } = this.props
    const isAdmin = !!relationships ? relationships.get('admin') : false
    const isModerator = !!relationships ? relationships.get('moderator') : false
    const isMember = !!relationships ? relationships.get('member') || isAdmin || isModerator : false
    this.props.dispatch(openPopover(POPOVER_GROUP_OPTIONS, {
      targetRef: this.infoBtn,
      group,
      isAdmin,
      isModerator,
      isMember,
    }))
  }

  setInfoBtn = (c) => {
    this.infoBtn = c
  }

  render() {
    const {
      children,
      group,
      relationships,
      isTimeline,
      moderationCount,
      requestsCount,
      myWaitingCount,
      forwardGroupId,
    } = this.props

    const groupTitle = !!group ? group.get('title') : ''
    const groupId = !!group ? group.get('id') : undefined

    const isAdmin = !!relationships ? relationships.get('admin') : false
    const isPrivate = !!group ? group.get('is_private') : false
    const isMember = !!relationships ? relationships.get('member') : false
    const unavailable = isPrivate && !isMember

    if (!!group) {
      if (group.get('archived')) return null
    }

    const myStatsModerationMessage = typeof myWaitingCount === 'number' && myWaitingCount > 0 && isTimeline && (
        <NavLink
          to={`/groups/${groupId}/moderation`}
          className={[_s.mt15, _s.mb5, _s.cursorPointer, _s.noUnderline].join(' ')}
        >
          <Block>
            <Text className={[_s.px10, _s.py10].join(' ')}>
              <Badge text={myWaitingCount} /> {pluralize('message', myWaitingCount)} awaiting approval by{' '}
              {groupTitle} moderators. →
            </Text>
          </Block>
        </NavLink>
      )
    
    const requestsMessage = typeof requestsCount === 'number' && requestsCount > 0 && isTimeline && (
        <NavLink
          to={`/groups/${groupId}/requests`}
          className={[_s.mt15, _s.mb5, _s.cursorPointer, _s.noUnderline].join(' ')}
        >
          <Block>
            <Text className={[_s.px10, _s.py10].join(' ')}>
              <Badge text={requestsCount} /> {pluralize('account', requestsCount)} awaiting approval to join {groupTitle}. →
            </Text>
          </Block>
        </NavLink>
      )

    const sidebarChildren = [
      isAdmin && <GroupAdminPanel noPanel groupId={groupId} />,
      <ShortcutsPanel type='group' noPanel />,
      <GroupsPanel groupType='member' max={20} noPanel padded />,
    ].filter(Boolean)
    
    const actions = !!me ? [
      {
        icon: 'ellipsis',
        buttonRef: this.setInfoBtn,
        onClick: this.handleOnOpenGroupOptions,
      }
    ] : []

    return (
      <GroupLayout
        title={'Group'}
        group={group}
        groupId={groupId}
        actions={actions}
        relationships={relationships}
        moderationCount={moderationCount}
        requestsCount={requestsCount}
        showSidebar={isTimeline}
        sidebarChildren={<SidebarPanelGroup layout={sidebarChildren} />}
      >
        <PageTitle path={[groupTitle, 'Group']} />

        {!!relationships && isTimeline && isMember && (
          <React.Fragment>
            <ComposeForm isSlim composerId={`group-${groupId}`} groupId={groupId} />
          </React.Fragment>
        )}

        {unavailable && (
          <Block>
            <ColumnIndicator
              type="error"
              message='This group is private. You must request to join in order to view this group.'
            />
          </Block>
        )}

        {myStatsModerationMessage}
        {requestsMessage}

        {!unavailable && !forwardGroupId && children}
        {
          !unavailable && forwardGroupId &&
          <div className={[_s.d, _s.boxShadowBlock, _s.px15, _s.py15, _s.w100PC, _s.maxW640PX, _s.mlAuto, _s.mrAuto, _s.bgPrimary, _s.overflowHidden, _s.radiusSmall].join(' ')}>
            {React.cloneElement(children, { groupId, showForm: true })}
            </div>
        }
      </GroupLayout>
    )
  }
}

const mapStateToProps = (state, { params: { id } }) => ({
  group: state.getIn(['groups', id]),
  relationships: state.getIn(['group_relationships', id]),
  moderationCount: state.getIn(['group_lists', 'group_moderation', id, 'count']),
  requestsCount: state.getIn(['user_lists', 'group_join_requests', id, 'items'], ImmutableList()).size,
  myWaitingCount: state.getIn(['group_lists', 'my_stats', id, 'count'])
})

GroupPage.propTypes = {
  group: ImmutablePropTypes.map,
  children: PropTypes.node.isRequired,
  relationships: ImmutablePropTypes.map,
  dispatch: PropTypes.func.isRequired
}

export default connect(mapStateToProps)(GroupPage)
